import { skipToken } from "@reduxjs/toolkit/query";
import type { KnowledgeBase } from "@repo/app-types/entities/knowledgeBase";
import {
  knowledgeBaseSyncProviderLabels,
  maybeGetKnowledgeBaseExternalProvider,
} from "@repo/app-types/entities/knowledgeBaseSync";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import {
  IconFileText,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { type FC, type ReactNode, useCallback, useState } from "react";
import {
  useDeleteKnowledgeBaseAttachmentMutation,
  useGetKnowledgeBaseAttachmentsQuery,
} from "../../../api/entities/knowledgeBase.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { useKnowledgeBaseDocumentsUploader } from "../../settingsPage/callRecorderSettings/useKnowledgeBaseDocumentsUploader.js";

export interface KnowledgeBaseSectionProps {
  knowledgeBaseIds: readonly string[];
  allKnowledgeBases: readonly KnowledgeBase[];
  onKnowledgeBaseIdsChange: (ids: string[]) => void;
  onEditAttachment: (knowledgeBaseId: string, attachmentId: string) => void;
  banner?: ReactNode;
}

export const KnowledgeBaseSection: FC<KnowledgeBaseSectionProps> = ({
  knowledgeBaseIds,
  allKnowledgeBases,
  onKnowledgeBaseIdsChange,
  onEditAttachment,
  banner,
}) => {
  const dispatch = useAppDispatch();
  const selectedKnowledgeBaseId = knowledgeBaseIds[0] ?? null;
  const [pendingDeleteAttachmentId, setPendingDeleteAttachmentId] = useState<
    string | null
  >(null);

  const { data: attachments } = useGetKnowledgeBaseAttachmentsQuery(
    selectedKnowledgeBaseId ?? skipToken,
  );

  const [deleteAttachment] = useDeleteKnowledgeBaseAttachmentMutation();

  const options: readonly SimpleSelectOption<string | null>[] = [
    { value: null, label: "No knowledge base" },
    ...allKnowledgeBases.map((kb) => ({
      value: kb.id,
      label: kb.name,
    })),
  ];

  // Sync-owned KBs are read-only: no adding, editing, or deleting articles
  // (still selectable for the agent, and articles can be viewed).
  const selectedKnowledgeBase = allKnowledgeBases.find(
    (kb) => kb.id === selectedKnowledgeBaseId,
  );
  const syncedProvider = maybeGetKnowledgeBaseExternalProvider(
    selectedKnowledgeBase?.metadata,
  );

  const handleKnowledgeBaseChange = (value: string | null) => {
    onKnowledgeBaseIdsChange(value ? [value] : []);
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!selectedKnowledgeBaseId) return;
    try {
      await deleteAttachment({
        knowledgeBaseId: selectedKnowledgeBaseId,
        attachmentId,
      }).unwrap();
      showNotification(dispatch, {
        id: `attachment-delete-${Date.now().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Article deleted",
      });
    } catch {
      showNotification(dispatch, {
        id: `attachment-delete-error-${Date.now().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to delete article",
        secondaryMessage: "Please try again.",
      });
    }
  };

  const handleUploadSuccess = useCallback(
    (upload: { id: string; name: string }) => {
      showNotification(dispatch, {
        id: `attachment-upload-${Date.now().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Article uploaded",
        secondaryMessage: `${upload.name} has been uploaded.`,
      });
    },
    [dispatch],
  );

  const handleUploadError = useCallback(
    (fileName: string) => {
      showNotification(dispatch, {
        id: `attachment-upload-error-${Date.now().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to upload article",
        secondaryMessage: `Could not upload ${fileName}. Please try again.`,
      });
    },
    [dispatch],
  );

  const { openFileDialog, uploadHiddenInput, isUploading } =
    useKnowledgeBaseDocumentsUploader({
      knowledgeBaseId: selectedKnowledgeBaseId ?? "",
      onUploadSuccess: handleUploadSuccess,
      onUploadError: handleUploadError,
    });

  return (
    <div className="space-y-4">
      <div className="max-w-[360px] min-w-[240px]">
        <SimpleSelect
          label="Knowledge base"
          isLabelHidden
          options={options}
          value={selectedKnowledgeBaseId}
          onChange={handleKnowledgeBaseChange}
          placeholder="Select a knowledge base..."
        />
      </div>

      {selectedKnowledgeBaseId && attachments && attachments.length > 0 && (
        <div className="divide-y divide-gray-100 rounded-lg border border-border-light">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="group flex items-center gap-2 px-4 py-2"
            >
              <IconFileText className="h-5 w-5 shrink-0 text-gray-400" />
              <span className="flex-1 truncate text-sm font-medium text-foreground">
                {attachment.title ?? attachment.file_name}
              </span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                <IconButton
                  icon={<IconPencil className="h-4 w-4" />}
                  variant={ButtonVariant.Secondary}
                  onClick={() =>
                    onEditAttachment(selectedKnowledgeBaseId, attachment.id)
                  }
                  accessibleLabel={`${syncedProvider ? "View" : "Edit"} ${attachment.title ?? attachment.file_name}`}
                />
                {!syncedProvider ? (
                  <IconButton
                    icon={<IconTrash className="h-4 w-4" />}
                    variant={ButtonVariant.Secondary}
                    onClick={() => setPendingDeleteAttachmentId(attachment.id)}
                    accessibleLabel={`Delete ${attachment.title ?? attachment.file_name}`}
                  />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedKnowledgeBaseId && syncedProvider ? (
        <p className="text-sm text-muted-foreground">
          Synced from {knowledgeBaseSyncProviderLabels[syncedProvider.provider]}{" "}
          — read-only.
        </p>
      ) : null}

      {selectedKnowledgeBaseId && !syncedProvider && (
        <>
          {uploadHiddenInput}
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            icon={<IconPlus className="h-4 w-4" />}
            onClick={openFileDialog}
            isLoading={isUploading}
            isDisabled={isUploading}
          >
            Add article
          </Button>
        </>
      )}

      {banner}

      <ConfirmationModal
        isOpen={pendingDeleteAttachmentId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDeleteAttachmentId(null);
          }
        }}
        title="Delete article?"
        confirmText="Delete"
        onConfirm={() => {
          if (pendingDeleteAttachmentId) {
            handleDeleteAttachment(pendingDeleteAttachmentId);
          }
          setPendingDeleteAttachmentId(null);
        }}
      >
        This article will be permanently removed from the knowledge base.
      </ConfirmationModal>
    </div>
  );
};
